import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { withLocalePrefix } from 'shared/utils/translation';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { PriorityCandidatesContent } from 'talent_matching/components/filters/priority_candidates';
import { ManualReviewContent } from 'talent_matching/components/filters/manual_review';
import { ExperienceContent } from 'talent_matching/components/filters/experience';
import { useExperienceState } from 'talent_matching/components/filters/use_experience_state';
import EducationFiltersSection from 'talent_matching/components/filters/education_filters';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';

const t = withLocalePrefix('talent_matching.filter_row.grouped_buttons');

const CANDIDATE_POPOVER_WIDTH_WITH_RESUME_PARSING = '520px';
const CANDIDATE_POPOVER_WIDTH_WITH_EDUCATION_FILTERS = '700px';
const CANDIDATE_POPOVER_WIDTH_WITHOUT_RESUME_PARSING = '330px';

const CandidateFiltersGroup = () => {
  const { canParseResumes, canViewTalentMatchingAiMatchScore } =
    useUserContext();
  const { watch, setValue } = useFormContext();
  const experienceState = useExperienceState();
  const [priorityCandidates, manualReview, educationFilters] = watch([
    'priorityCandidates',
    'manualReview',
    'educationFilters',
  ]);

  const hasExperience =
    canParseResumes && experienceState.totalFiltersCount > 0;

  const hasManualReview =
    canViewTalentMatchingAiMatchScore && Boolean(manualReview);

  const appliedEducationFilters = (educationFilters as AdvancedFilter[]) || [];
  const hasEducationFilters =
    canParseResumes &&
    appliedEducationFilters.some((filter: AdvancedFilter) => filter.applied());

  const popoverWidth = (() => {
    if (!canParseResumes) {
      return CANDIDATE_POPOVER_WIDTH_WITHOUT_RESUME_PARSING;
    }

    if (appliedEducationFilters.length > 0) {
      return CANDIDATE_POPOVER_WIDTH_WITH_EDUCATION_FILTERS;
    }

    return CANDIDATE_POPOVER_WIDTH_WITH_RESUME_PARSING;
  })();

  const appliedCount = [
    priorityCandidates,
    hasManualReview,
    hasExperience,
    hasEducationFilters,
  ].filter(Boolean).length;

  const buttonLabel = `${t('candidate')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  const clear = () => {
    setValue('priorityCandidates', false, { shouldDirty: true });
    setValue('manualReview', false, { shouldDirty: true });
    experienceState.clear();
    setValue('educationFilters', [], { shouldDirty: true });
  };

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('candidate_filters')}
      width={popoverWidth}
    >
      <Stack spacing={3}>
        <PriorityCandidatesContent />
        {canViewTalentMatchingAiMatchScore && <ManualReviewContent />}
        {canParseResumes && (
          <Stack spacing={3} alignItems="flex-start">
            <ExperienceContent state={experienceState} />
            <EducationFiltersSection />
          </Stack>
        )}
        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default CandidateFiltersGroup;
